import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  CheckOutlined,
  DownloadOutlined,
  FileTextOutlined,
  HistoryOutlined,
} from '@ant-design/icons';

import { Badge, Button, Empty, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, dungLuongVN, ngayGioVN, ngayVN } from '../../../lib/dinh-dang';
import { maLoi } from '../../../lib/errors';
import { CanhBaoTrongHop, LoiTrongHop } from '../../tenant/components/phan-form';
import { LOAI_TEP, QUYEN, QUYEN_THAO_TAC_TAI_LIEU, THAO_TAC_TAI_LIEU, THU_TU_TRANG_THAI_TAI_LIEU, TRANG_THAI_TAI_LIEU } from '../constants';
import type { ThaoTacCoHop } from '../constants';
import { useChiTietTaiLieu, useChuyenTaiLieu, useTaiXuong } from '../hooks/useTriThuc';
import { thaoTacDuoc } from '../nghiep-vu/tri-thuc';
import type { ThaoTacTaiLieu } from '../nghiep-vu/tri-thuc';
import type { KnowledgeDocumentDetail } from '../types';
import { FormPhienBan, HopThaoTacTaiLieu } from './HopTaiLieu';

/** Thao tác chính của từng trạng thái — nút đặc, các nút còn lại viền. */
const CHINH: ReadonlySet<ThaoTacTaiLieu> = new Set(['submit-review', 'approve']);

/** Khung bên phải màn kiểm duyệt: thông tin, tiến trình, thao tác và lịch sử phiên bản của một tài liệu. */
export function ChiTietTaiLieu({ id }: { id: string }) {
  const { t } = useT();
  const dichLoi = useLoi();
  const q = useChiTietTaiLieu(id);

  if (q.isLoading) {
    return (
      <div className="space-y-4 p-5" aria-busy="true">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-24" />
        <Skeleton className="h-32" />
      </div>
    );
  }
  if (!q.data) {
    if (maLoi(q.error) === 'NOT_FOUND' || q.error?.status === 404) {
      return <Empty icon={FileTextOutlined} title={t('triThuc.taiLieu.khongThay')} />;
    }
    return (
      <div role="alert" className="flex flex-wrap items-center justify-between gap-3 p-5">
        <span className="min-w-0 text-sm text-danger-700">{t('triThuc.taiLieu.loiChiTiet', { loi: dichLoi(q.error) })}</span>
        <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  }
  // `key`: đổi tài liệu là bỏ mọi hộp và lỗi đang mở của tài liệu trước.
  return <NoiDung key={q.data.id} d={q.data} />;
}

function NoiDung({ d }: { d: KnowledgeDocumentDetail }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const quyen = useModulePermissions(QUYEN);
  const chuyen = useChuyenTaiLieu();
  const taiXuong = useTaiXuong();
  const [hop, setHop] = useState<ThaoTacCoHop | 'phien-ban' | null>(null);

  const trangThai = TRANG_THAI_TAI_LIEU[d.status];
  const viTri = THU_TU_TRANG_THAI_TAI_LIEU.indexOf(d.status);
  const thaoTac = thaoTacDuoc(d.status).filter((x) => quyen[QUYEN_THAO_TAC_TAI_LIEU[x]]);
  const phienBan = [...d.versions].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const tomTat = phienBan.find((v) => v.version_number === d.current_version)?.content_summary ?? null;

  const bam = (x: ThaoTacTaiLieu) => {
    // Gửi duyệt không có hộp (`ThaoTacCoHop`).
    if (x === 'submit-review') chuyen.mutate({ taiLieu: d, thaoTac: x });
    else setHop(x);
  };

  return (
    <div className="space-y-5 p-5">
      <header className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h2 className="min-w-0 break-words text-base font-semibold text-content-primary">{d.title}</h2>
          <Badge variant={trangThai.variant}>{t(trangThai.khoa)}</Badge>
        </div>
        <dl className="grid gap-x-6 gap-y-1.5 text-xs sm:grid-cols-2">
          <ThongTin nhan={t('triThuc.taiLieu.bo')} giaTri={d.knowledge_base_name} />
          <ThongTin nhan={t('triThuc.taiLieu.phienBan')} giaTri={<span className="font-mono">{d.current_version}</span>} />
          <ThongTin
            nhan={t('triThuc.taiLieu.tep')}
            giaTri={d.has_file ? `${t(LOAI_TEP[d.file_type])} · ${dungLuongVN(d.file_size_bytes, locale)}` : t('triThuc.taiLieu.khongKemTep')}
          />
          <ThongTin nhan={t('triThuc.taiLieu.ngayTai')} giaTri={ngayVN(d.uploaded_at)} />
          <ThongTin nhan={t('triThuc.taiLieu.capNhat')} giaTri={ngayGioVN(d.updated_at)} />
          {d.reviewed_at && <ThongTin nhan={t('triThuc.taiLieu.xetLuc')} giaTri={ngayGioVN(d.reviewed_at)} />}
        </dl>
      </header>

      <ol aria-label={t('triThuc.taiLieu.tienTrinh')} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        {THU_TU_TRANG_THAI_TAI_LIEU.map((s, i) => (
          <li key={s} className="flex items-center gap-2" aria-current={i === viTri ? 'step' : undefined}>
            {i > 0 && <span aria-hidden className="w-6 border-t border-strong" />}
            <span
              className={
                i === viTri
                  ? 'inline-flex items-center gap-1 rounded bg-primary-50 px-2 py-1 font-semibold text-primary-700'
                  : i < viTri
                    ? 'inline-flex items-center gap-1 px-2 py-1 text-content-secondary'
                    : 'inline-flex items-center gap-1 px-2 py-1 text-content-muted'
              }
            >
              {i < viTri && <CheckOutlined aria-hidden className="text-success-600" />}
              {t(TRANG_THAI_TAI_LIEU[s].khoa)}
            </span>
          </li>
        ))}
      </ol>

      {d.review_note && (
        <CanhBaoTrongHop>
          <span className="block font-medium">
            {d.reviewed_at
              ? t('triThuc.taiLieu.ghiChuXetLuc', { ngay: ngayVN(d.reviewed_at) })
              : t('triThuc.taiLieu.ghiChuXet')}
          </span>
          <span className="block whitespace-pre-line">{d.review_note}</span>
        </CanhBaoTrongHop>
      )}

      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {thaoTac.map((x) => (
            <Button
              key={x}
              variant={CHINH.has(x) ? 'primary' : 'outline'}
              size="sm"
              loading={x === 'submit-review' && chuyen.isPending}
              onClick={() => bam(x)}
            >
              {t(THAO_TAC_TAI_LIEU[x].nhan)}
            </Button>
          ))}
          {quyen.taiLieuTao && (
            <Button variant="outline" size="sm" icon={<HistoryOutlined />} onClick={() => setHop('phien-ban')}>
              {t('triThuc.taiLieu.taoPhienBan')}
            </Button>
          )}
          {d.has_file && (
            <Button
              variant="ghost"
              size="sm"
              icon={<DownloadOutlined />}
              loading={taiXuong.isPending}
              onClick={() => taiXuong.mutate({ loai: 'tai-lieu', id: d.id, ten: d.title })}
            >
              {t('triThuc.taiLieu.taiXuong')}
            </Button>
          )}
        </div>
        {chuyen.error && <LoiTrongHop>{dichLoi(chuyen.error)}</LoiTrongHop>}
      </div>

      <section className="space-y-1.5">
        <h3 className="text-sm font-semibold text-content-primary">{t('triThuc.taiLieu.tomTat')}</h3>
        <p className="whitespace-pre-line text-sm leading-relaxed text-content-secondary">
          {tomTat || <span className="text-content-muted">{t('triThuc.taiLieu.chuaCoTomTat')}</span>}
        </p>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-content-primary">{t('triThuc.taiLieu.lichSu')}</h3>
        {phienBan.length === 0 ? (
          <p className="text-xs text-content-muted">{t('triThuc.taiLieu.chuaCoLichSu')}</p>
        ) : (
          <ol className="divide-y divide-default rounded border border-default">
            {phienBan.map((v) => (
              <li key={v.id} className="flex flex-wrap items-start gap-3 px-3 py-2.5">
                <span className="w-12 shrink-0 font-mono text-xs font-semibold text-content-primary">{v.version_number}</span>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="break-words text-sm text-content-secondary">{v.change_note || t('triThuc.taiLieu.khongGhiChu')}</p>
                  <p className="text-2xs text-content-muted">{ngayGioVN(v.created_at)}</p>
                </div>
                <Badge variant={TRANG_THAI_TAI_LIEU[v.status].variant}>{t(TRANG_THAI_TAI_LIEU[v.status].khoa)}</Badge>
              </li>
            ))}
          </ol>
        )}
      </section>

      {hop === 'phien-ban' && <FormPhienBan taiLieu={d} open onClose={() => setHop(null)} />}
      {hop && hop !== 'phien-ban' && <HopThaoTacTaiLieu taiLieu={d} thaoTac={hop} open onClose={() => setHop(null)} />}
    </div>
  );
}

function ThongTin({ nhan, giaTri }: { nhan: string; giaTri: ReactNode }) {
  return (
    <div className="flex min-w-0 gap-1.5">
      <dt className="shrink-0 text-content-muted">{nhan}</dt>
      <dd className="min-w-0 truncate text-content-secondary">{giaTri || KHONG_CO}</dd>
    </div>
  );
}
